import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { fmtMoney0, fmtMoney, fmtDate } from "../lib/format";
import { genererBonPDF } from "../lib/pdf";
import { Modal, Field, Badge, Spinner, EmptyState, Confirm } from "../components/ui";
import { SearchSelect, orgOptions } from "../components/SearchSelect";

const BC_STATUTS = ["Émis", "Confirmé", "Livré", "Facturé", "Annulé"];
const TVA_RATES = [0, 7, 13, 19];

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, isNaN(v) ? min : v));

function computeTotals(lignes: any[], remiseGlob: number, tvaPct: number, retenuePct: number) {
  let net = 0;
  for (const l of lignes) {
    if ((l.type || "ligne") !== "ligne") continue;
    net += (+l.qte || 0) * (+l.prix_unitaire || 0) * (1 - (+l.remise_pct || 0) / 100);
  }
  const rg = (net * (+remiseGlob || 0)) / 100;
  const ht = net - rg;
  const tva = (ht * (+tvaPct || 0)) / 100;
  const ttc = ht + tva;
  const retenue = (ttc * (+retenuePct || 0)) / 100;
  return { net, rg, ht, tva, ttc, retenue, napayer: ttc - retenue };
}

function BonForm({ bonId, orgs, entites, devisList, onClose, reload }: any) {
  const [b, setB] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (bonId) {
      api.get(`/bons-commande/${bonId}`).then((r) => setB(r.data));
    } else {
      setB({ statut: "Émis", devise: "EUR", remise_globale_pct: 35, tva_pct: 0, retenue_pct: 0, paye: false, lignes: [{ type: "ligne", designation: "", qte: 1, prix_unitaire: 0, remise_pct: 0 }], date: new Date().toISOString().slice(0, 10) });
    }
  }, [bonId]);
  if (!b) return null;
  const linked = !!b.devis_id;
  const totals = computeTotals(b.lignes || [], b.remise_globale_pct, b.tva_pct, b.retenue_pct);

  const save = async () => {
    if (saving) return;
    setErr(null);
    setSaving(true);
    try {
      if (b.id) await api.put(`/bons-commande/${b.id}`, b);
      else await api.post("/bons-commande", b);
      reload();
      onClose();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateLigne = (i: number, patch: any) => {
    const lignes = [...b.lignes];
    lignes[i] = { ...lignes[i], ...patch };
    setB({ ...b, lignes });
  };

  return (
    <Modal title={b.id ? `Bon de commande ${b.numero}` : "Nouveau bon de commande"} onClose={onClose} wide testId="bon-modal">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label="Devis lié">
          <select data-testid="bon-devis-select" className="nx-input" value={b.devis_id || ""} onChange={(e) => setB({ ...b, devis_id: e.target.value || null })}>
            <option value="">— Aucun (lignes propres) —</option>
            {devisList.map((d: any) => <option key={d.id} value={d.id}>N° {d.numero} — {d.objet}</option>)}
          </select>
        </Field>
        <Field label="Fournisseur">
          <SearchSelect testId="bon-fournisseur-select" options={orgOptions(orgs.filter((o: any) => o.type === "fournisseur"))} value={b.fournisseur_id || null} onChange={(v) => setB({ ...b, fournisseur_id: v })} />
        </Field>
        <Field label="Client final">
          <SearchSelect testId="bon-clientfinal-select" options={orgOptions(orgs.filter((o: any) => o.type === "client" || o.type === "revendeur"))} value={b.client_final_id || null} onChange={(v) => setB({ ...b, client_final_id: v })} />
        </Field>
        <Field label="Revendeur">
          <SearchSelect testId="bon-revendeur-select" options={orgOptions(orgs.filter((o: any) => o.type === "revendeur"))} value={b.revendeur_id || null} onChange={(v) => setB({ ...b, revendeur_id: v })} />
        </Field>
        <Field label="Entité">
          <select data-testid="bon-entite-select" className="nx-input" value={b.entite_id || ""} onChange={(e) => setB({ ...b, entite_id: e.target.value || null })}>
            <option value="">—</option>
            {entites.map((e: any) => <option key={e.id} value={e.id}>{e.code}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input type="date" className="nx-input" value={(b.date || "").slice(0, 10)} onChange={(e) => setB({ ...b, date: e.target.value })} />
        </Field>
        <Field label="Statut">
          <select data-testid="bon-statut-select" className="nx-input" value={b.statut} onChange={(e) => setB({ ...b, statut: e.target.value })}>
            {BC_STATUTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Référence">
          <input className="nx-input" value={b.reference || ""} onChange={(e) => setB({ ...b, reference: e.target.value })} />
        </Field>
        <Field label="Objet" className="col-span-2">
          <input data-testid="bon-objet-input" className="nx-input" value={b.objet || ""} onChange={(e) => setB({ ...b, objet: e.target.value })} />
        </Field>
        <Field label="Devise">
          <select className="nx-input" value={b.devise} onChange={(e) => setB({ ...b, devise: e.target.value })}>
            <option>EUR</option>
            <option>TND</option>
          </select>
        </Field>
        <Field label="Marge">
          <input data-testid="bon-marge-input" type="number" step="0.01" className="nx-input" value={b.marge ?? ""} onChange={(e) => setB({ ...b, marge: e.target.value === "" ? null : +e.target.value })} />
        </Field>
        <Field label="Remise globale %">
          <input data-testid="bon-remise-input" type="number" min="0" max="100" className="nx-input" value={b.remise_globale_pct ?? 35} onChange={(e) => setB({ ...b, remise_globale_pct: clamp(+e.target.value, 0, 100) })} />
        </Field>
        <Field label="TVA %">
          <select data-testid="bon-tva-select" className="nx-input" value={b.tva_pct ?? 0} onChange={(e) => setB({ ...b, tva_pct: +e.target.value })}>
            {TVA_RATES.map((t) => <option key={t} value={t}>{t} %</option>)}
          </select>
        </Field>
        <Field label="Retenue à la source %">
          <select className="nx-input" value={b.retenue_pct ?? 0} onChange={(e) => setB({ ...b, retenue_pct: +e.target.value })}>
            <option value={0}>0 %</option>
            <option value={1}>1 %</option>
          </select>
        </Field>
        <Field label="Payé">
          <label className="flex items-center gap-2 pt-2 text-sm">
            <input data-testid="bon-paye-checkbox" type="checkbox" checked={!!b.paye} onChange={(e) => setB({ ...b, paye: e.target.checked })} />
            Marqué payé (tampon PAYÉ)
          </label>
        </Field>
        <Field label="Note" className="col-span-2">
          <input className="nx-input" value={b.note || ""} onChange={(e) => setB({ ...b, note: e.target.value })} />
        </Field>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-green">Lignes {linked && "(reprises du devis lié — remise et TVA propres au BC appliquées)"}</h3>
          {!linked && (
            <button data-testid="bon-ligne-add-btn" className="nx-btn-ghost !px-2.5 !py-1 text-xs" onClick={() => setB({ ...b, lignes: [...b.lignes, { type: "ligne", designation: "", qte: 1, prix_unitaire: 0, remise_pct: 0 }] })}>
              + Ligne
            </button>
          )}
        </div>
        {!linked ? (
          <div className="space-y-1.5">
            {(b.lignes || []).length > 0 && (
              <div data-testid="bon-lignes-header" className="flex items-center gap-2 text-xs font-semibold text-muted">
                <span className="flex-1">Désignation</span>
                <span className="w-20 text-right">Qté</span>
                <span className="w-28 text-right">P.U.</span>
                <span className="w-20 text-right">Remise %</span>
                <span className="w-28 text-right">Montant</span>
                <span className="w-[22px]" />
              </div>
            )}
            {(b.lignes || []).map((l: any, i: number) => (
              <div key={i} className="flex items-center gap-2">
                <input data-testid={`bon-ligne-designation-${i}`} className="nx-input flex-1" placeholder="Désignation" value={l.designation || ""} onChange={(e) => updateLigne(i, { designation: e.target.value })} />
                <input data-testid={`bon-ligne-qte-${i}`} type="number" min="0" className="nx-input w-20 text-right" value={l.qte ?? ""} onChange={(e) => updateLigne(i, { qte: clamp(+e.target.value, 0, 1e9) })} />
                <input data-testid={`bon-ligne-pu-${i}`} type="number" min="0" step="0.01" className="nx-input w-28 text-right" value={l.prix_unitaire ?? ""} onChange={(e) => updateLigne(i, { prix_unitaire: clamp(+e.target.value, 0, 1e12) })} />
                <input data-testid={`bon-ligne-remise-${i}`} type="number" min="0" max="100" className="nx-input w-20 text-right" placeholder="Remise %" value={l.remise_pct ?? ""} onChange={(e) => updateLigne(i, { remise_pct: clamp(+e.target.value, 0, 100) })} />
                <span data-testid={`bon-ligne-montant-${i}`} className="w-28 text-right text-sm font-semibold">{fmtMoney((+l.qte || 0) * (+l.prix_unitaire || 0) * (1 - (+l.remise_pct || 0) / 100), b.devise)}</span>
                <button className="px-1.5 text-danger" onClick={() => setB({ ...b, lignes: b.lignes.filter((_: any, x: number) => x !== i) })}>✕</button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted">{(b.lignes || []).filter((l: any) => l.type === "ligne").length} ligne(s) reprises du devis N° {b.devis_numero || ""}</p>
        )}
        <div className="mt-3 ml-auto w-64 space-y-1 rounded-lg bg-soft p-3 text-sm" data-testid="bon-totaux">
          <div className="flex justify-between"><span className="text-muted">Total HT</span><span>{fmtMoney(totals.net, b.devise)}</span></div>
          <div className="flex justify-between"><span className="text-muted">Remise ({b.remise_globale_pct} %)</span><span>- {fmtMoney(totals.rg, b.devise)}</span></div>
          <div className="flex justify-between font-semibold"><span>Net HT</span><span>{fmtMoney(totals.ht, b.devise)}</span></div>
          <div className="flex justify-between"><span className="text-muted">TVA ({b.tva_pct} %)</span><span>{fmtMoney(totals.tva, b.devise)}</span></div>
          {totals.retenue > 0 && <div className="flex justify-between"><span className="text-muted">Retenue ({b.retenue_pct} %)</span><span>- {fmtMoney(totals.retenue, b.devise)}</span></div>}
          <div className="flex justify-between border-t border-line pt-1 font-extrabold text-green"><span>NET À PAYER</span><span>{fmtMoney(totals.napayer, b.devise)}</span></div>
        </div>
      </div>

      {err && <p className="mt-2 text-sm text-danger" data-testid="bon-form-error">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="nx-btn-ghost" onClick={onClose}>Annuler</button>
        <button data-testid="bon-save-btn" className="nx-btn-primary" disabled={saving} onClick={save}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
      </div>
    </Modal>
  );
}

export default function Bons() {
  const [bons, setBons] = useState<any[] | null>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [entites, setEntites] = useState<any[]>([]);
  const [devisList, setDevisList] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [del, setDel] = useState<any>(null);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);
  const [pageErr, setPageErr] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/bons-commande").then((r) => setBons(r.data));
  useEffect(() => {
    load();
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/entites").then((r) => setEntites(r.data));
    api.get("/devis").then((r) => setDevisList(r.data));
  }, []);

  useEffect(() => {
    const bon = params.get("bon");
    if (bon) {
      setEdit({ id: bon });
      params.delete("bon");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  if (!bons) return <Spinner />;

  const genPdf = async (bon: any) => {
    if (pdfBusy) return;
    setPdfBusy(bon.id);
    setPageErr(null);
    try {
      const { data: full } = await api.get(`/bons-commande/${bon.id}`);
      if (!full.fournisseur_id) {
        setPageErr(`BC ${bon.numero} : sélectionnez un fournisseur avant de générer le bon de commande.`);
        return;
      }
      if (!(full.lignes || []).filter((l: any) => l.type === "ligne").length) {
        setPageErr(`BC ${bon.numero} : ajoutez au moins une ligne avant de générer le bon de commande.`);
        return;
      }
      const entite = entites.find((e) => e.id === full.entite_id);
      if (!entite) {
        setPageErr(`BC ${bon.numero} : sélectionnez une entité de facturation.`);
        return;
      }
      const frs = orgs.find((o) => o.id === full.fournisseur_id);
      await genererBonPDF(full, frs, entite);
      load();
    } catch (e) {
      setPageErr(`Échec de la génération du PDF : ${apiError(e)}`);
    } finally {
      setPdfBusy(null);
    }
  };

  return (
    <div data-testid="bons-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-green">Bons de commande</h1>
        <button data-testid="bon-new-btn" className="nx-btn-primary" onClick={() => setEdit({})}>Nouveau BC</button>
      </div>
      {pageErr && <p data-testid="bons-error" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{pageErr}</p>}
      <div className="nx-card overflow-x-auto">
        {bons.length === 0 ? <EmptyState message="Aucun bon de commande" /> : (
          <table className="nx-table">
            <thead><tr><th>N°</th><th>Devis</th><th>Fournisseur</th><th>Client final</th><th>Entité</th><th>Date</th><th>Statut</th><th className="text-right">Net à payer</th><th className="text-right">Marge</th><th>Payé</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {bons.map((b) => (
                <tr key={b.id} data-testid={`bon-row-${b.numero}`}>
                  <td className="font-extrabold text-green">{b.numero}</td>
                  <td>{b.devis_numero ? `N° ${b.devis_numero}` : "—"}</td>
                  <td>{b.fournisseur_nom}</td>
                  <td>{b.client_final_nom}</td>
                  <td>{b.entite_code && <Badge className="bg-green text-white">{b.entite_code}</Badge>}</td>
                  <td>{fmtDate(b.date)}</td>
                  <td><Badge className={b.statut === "Facturé" ? "bg-lime-soft text-green" : b.statut === "Annulé" ? "bg-red-50 text-danger" : "bg-soft text-muted"}>{b.statut}</Badge></td>
                  <td className="text-right font-semibold">{fmtMoney0(b.net_a_payer, b.devise)}</td>
                  <td className="text-right">{fmtMoney0(b.marge)}</td>
                  <td>{b.paye ? <Badge className="bg-green text-white">PAYÉ</Badge> : "—"}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`bon-pdf-btn-${b.numero}`} className="nx-btn-lime !px-2 !py-1 text-xs" disabled={!!pdfBusy || !entites.length || !orgs.length} onClick={() => genPdf(b)}>{pdfBusy === b.id ? "Génération…" : "PDF"}</button>
                      <button
                        data-testid={`bon-payer-btn-${b.numero}`}
                        className="nx-btn-ghost !px-2 !py-1 text-xs"
                        onClick={async () => {
                          await api.patch(`/bons-commande/${b.id}/payer?paye=${!b.paye}`);
                          load();
                        }}
                      >
                        {b.paye ? "Marquer impayé" : "Marquer payé"}
                      </button>
                      <button data-testid={`bon-edit-btn-${b.numero}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEdit({ id: b.id })}>Éditer</button>
                      <button data-testid={`bon-delete-btn-${b.numero}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDel(b)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {edit && <BonForm bonId={edit.id} orgs={orgs} entites={entites} devisList={devisList} reload={load} onClose={() => setEdit(null)} />}
      {del && (
        <Confirm
          message={`Supprimer (archiver) le bon ${del.numero} ?`}
          onConfirm={async () => {
            await api.delete(`/bons-commande/${del.id}`);
            setDel(null);
            load();
          }}
          onCancel={() => setDel(null)}
        />
      )}
    </div>
  );
}
